import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { useProductQualityTeamId } from "@coworker/app/src/hooks/useProductQualityTeamId";
import { ProductQualityError } from "@coworker/apprestructured/src/shared/components/ProductQualityError/productQualityError";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { DefaultScannerComponent } from "@ingka-group-digital/flexa-scanner";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router";
import { ScanAndSearchSlot } from "../../ScanAndSearchSlot";
import { ExtractedArticleInformationType } from "../../types/ExtractedArticleInformationType";
import { CreateMoreAddonOrdersModal } from "./CreateMoreAddonOrdersModal/CreateMoreAddonOrdersModal";

const DefaultScannerPage: React.FC = () => {
  const navigate = useFixaNavigate();
  const { type } = useParams<{ type: string }>();
  const enableAddonOrdersV2 = useFlag(FLAGS.ENABLE_ADDON_ORDERS_V2);
  const [, setSearchParams] = useSearchParamState();
  const { data: taskTypes } = useAllTaskTypes();
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const productQualityTeamId = useProductQualityTeamId();
  const [showProductQualityErrorModal, setShowProductQualityErrorModal] =
    useState(false);

  const addonOrderId = useMemo(
    () => taskTypes?.find(({ name }) => name === TaskTypeNames.ADDON_ORDER)?.id,
    [taskTypes]
  );
  const productQualityId = useMemo(
    () =>
      taskTypes?.find(({ name }) => name === TaskTypeNames.PRODUCT_QUALITY)?.id,
    [taskTypes]
  );

  const handleResult = (result: ExtractedArticleInformationType) => {
    const articleType = result.articleType || "ART";
    if (type === undefined || type === "global") {
      navigate(`/product/${articleType}${result.articleNumber}`);
    } else if (type === "addon" && enableAddonOrdersV2 && addonOrderId) {
      setSearchParams((prev) => {
        prev.set("taskCreator", String(addonOrderId));
        prev.set(
          "initialValues",
          JSON.stringify({
            details: {
              item: {
                no: result.articleNumber,
                type: articleType,
              },
            },
          })
        );
      });
    } else if (type === "product_quality") {
      if (!productQualityTeamId) {
        setShowProductQualityErrorModal(true);
        return;
      }
      if (productQualityId) {
        setSearchParams((prev) => {
          prev.set("taskCreator", String(productQualityId));
          prev.set(
            "initialValues",
            JSON.stringify({
              details: {
                item: {
                  no: result.articleNumber,
                  type: articleType,
                },
              },
            })
          );
        });
      }
    } else {
      navigate(`/task/new/${type}`, {
        state: {
          product: {
            supplierNumber: result.supplierNumber,
            product: result.articleNumber,
            type: articleType,
          },
        },
      });
    }
  };

  const handleError = (message: string, context: Record<string, unknown>) => {
    reportMessageToSentry(message, context, "error");
  };

  return (
    <>
      <DefaultScannerComponent
        onResult={handleResult}
        isCameraOpen={isCameraOpen}
        onCameraOpenChange={setIsCameraOpen}
        withSearchPadding
        shouldShowLargeReadyToScan
        onError={handleError}
        searchSlot={
          <ScanAndSearchSlot
            onResult={handleResult}
            onCameraOpen={() => setIsCameraOpen(true)}
            onError={handleError}
          />
        }
      />
      {type === "addon" && <CreateMoreAddonOrdersModal />}
      <ProductQualityError
        openProductQualityErrorModal={showProductQualityErrorModal}
        onClose={setShowProductQualityErrorModal}
      />
    </>
  );
};

export default DefaultScannerPage;
